import { useState, useCallback, useRef, useEffect } from 'react';
import GlobalSVG from '../../utils/GlobalSVG';
import API from '../../utils/API';
import Swal from 'sweetalert2';
import UtilityBillLineUpload from '../UtilityBillLineUpload';
import Loader from '../Loader';
import LoaderWheel from "../LoaderWheel"
import * as XLSX from 'xlsx';
import Tooltip from "../../components/ToolTip";
import { uploadAndScan } from '../../utils/utils';

const FileUploadContainer = (props) => {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [ready, setReady] = useState(false)
  const [saving, setSaving] = useState(false)
  const multipleSites = useRef(null);

  const CSV_DICT = {
    "Account ID": "utility_bill_account_id",
    "Site ID": "site_id",
    "Bill ID": "bill_id",
    "Bill Month": "bill_month",
    "Bill Year": "bill_year",
    "Basic Monthly": "basic_monthly",
    "kWh Consumption": "kwh_consumption",
    "kWh Cost": "kwh_cost",
    "kVA Consumption": "kva_consumption",
    "kVA Cost": "kva_cost",
    "Water Consumption": "water_consumption",
    "Water Unit": "water_unit",
    "Water Cost": "water_cost",
    "Sewage Consumption": "sewage_consumption",
    "Sewage Cost": "sewage_cost",
    "Gas Consumption": "gas_consumption",
    "Gas Unit": "gas_unit",
    "Gas Cost": "gas_cost",
    "Delivery Charges": "delivery_charges",
    "Other Charges": "other_charges",
    "Carbon Charge": "carbon_charge",
    "Municipal Tax": "municipal_tax",
    "GST Cost": "gst_cost",
    "PST Cost": "pst_cost"
  }

  const fileInputRef = useRef(null);


  const processFiles = useCallback(async (newFiles) => {


    const EXTRACTION_CONCURRENCY = 3;
    const PDF_POLL_INTERVAL_MS = 10000;
    const PDF_POLL_MAX_ATTEMPTS = 60;
    const PAGE_DELIMITER_RE = /---\s*Page\s+(\d+)\s*---/gi;

    // Helper functions
    const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const base64Data = e.target.result;
            const isExcel =
              file.type ===
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
              file.type === 'application/vnd.ms-excel' ||
              file.name.toLowerCase().endsWith('.xlsx') ||
              file.name.toLowerCase().endsWith('.xls');
            let json = null;

            if (isExcel) {
              const base64 = base64Data.split(',')[1];
              const binary = atob(base64);

              const workbook = XLSX.read(binary, { type: 'binary' });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              json = XLSX.utils.sheet_to_json(worksheet, {
                defval: null, // keeps empty cells
              });
            }


            resolve({
              tempId: crypto.randomUUID(),
              name: file.name,
              type: file.type,
              size: file.size,
              data: base64Data,
              source: base64Data,
              json: json,
              progress: 0,
              status: 'Preparing...',
            });
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
    };

    const pollProcessedPdf = async (cancelToken, processIds) => {
      for (let attempt = 0; attempt < PDF_POLL_MAX_ATTEMPTS; attempt += 1) {
        const processedPdf = await API.getProcessedPdf(
          cancelToken,
          processIds.job_id,
          processIds.file_key
        );

        if (processedPdf.status === 200) return processedPdf.data;
        if (processedPdf.status !== 202) {
          throw new Error('PDF parsing failed');
        }
        await new Promise((resolve) => setTimeout(resolve, PDF_POLL_INTERVAL_MS));
      }
      throw new Error('PDF parsing timed out');
    };

    const processPdfFile = async (fileData, cancelToken, temporaryId) => {
      updateFileProgress(temporaryId, 30, 'Parsing PDF...');

      const processIds = (await API.processPdf(cancelToken, { data: fileData.data })).data;
      const pageTexts = await pollProcessedPdf(cancelToken, processIds);
      fileData.data = pageTexts;

      updateFileProgress(temporaryId, 50, 'Finding bills...');

      const pages = splitPages(pageTexts);
      fileData.pageCount = pages.length;

      let boundaries = {};
      try {
        boundaries = (await API.processPages(cancelToken, { data: pageTexts })).data || {};
      } catch (error) {
        console.error('Bill boundary detection failed', error);
      }
      
      const segments = sliceIntoBills(pages, boundaries.bills);
      fileData.billCount = segments.length;
      fileData.frontMatter = boundaries.front_matter || null;
      fileData.segments = segments || null;
    };



    const normalizeId = (id) => `${id}`.replace(/[.\-\s]/g, "");

    const matchBillsWithAccounts = async (bills, accounts, building) => {
      let newBills = []

      for (let item of bills) {
        item.utility_bill_account_id = normalizeId(item.utility_bill_account_id);
        item.site_id = item.site_id ? normalizeId(item.site_id) : null
        const matchedAccounts = accounts.filter(
          a => normalizeId(a.account_id) === item.utility_bill_account_id
        );
        if (matchedAccounts.length === 0) {
          item.utility_bill_account_id = null;
          item.type = 0;
          newBills.push(item)
          continue;
        }
        let hasSiteId = accounts.filter(a => a.site_id !== "N/A").length
        if (item.site_id && hasSiteId) {
          const matchedBuilding = accounts.filter(
            a => normalizeId(a.site_id) === item.site_id
          );

          if (matchedBuilding.length > 0) {
            multipleSites.current = null
            multipleSites.current ??= askMultipleSitesConfirmation();
            const confirmed = await multipleSites.current;

            if (confirmed === false && !matchedBuilding.filter(
              a => normalizeId(a.building_id) === props.building
            ).length) continue

            item.utility_bill_account_id = matchedBuilding[0].id;
            item.type = matchedBuilding[0].type;
            newBills.push(item)
          } else {
            item.utility_bill_account_id = null;
            item.type = 0;
            newBills.push(item);
          }
        } else {
          const buildingAccounts = accounts.filter(
            a => normalizeId(a.account_id) === item.utility_bill_account_id
          );

          if (buildingAccounts.length > 0) {
            item.utility_bill_account_id = buildingAccounts[0].id;
            item.type = buildingAccounts[0].type;
          } else {
            item.utility_bill_account_id = null;
            item.type = 0;
          }
          newBills.push(item)
        }
      }
      return newBills
    };


    const splitPages = (data) => {
      const text = String(data ?? '');
      const pages = [];
      let match;
      let lastIndex = null;
      let lastPage = null;
      PAGE_DELIMITER_RE.lastIndex = 0;
      while ((match = PAGE_DELIMITER_RE.exec(text)) !== null) {
        if (lastPage !== null) {
          pages.push({ page: lastPage, text: text.slice(lastIndex, match.index) });
        }
        lastPage = Number(match[1]);
        lastIndex = PAGE_DELIMITER_RE.lastIndex;
      }
      if (lastPage !== null) {
        pages.push({ page: lastPage, text: text.slice(lastIndex) });
      }
      // No delimiters found - treat the whole thing as one page.
      return pages.length ? pages : [{ page: 1, text }];
    };

    /** Re-emit pages in the same delimited format the extractor already expects. */
    const joinPages = (pages) =>
      pages.map(({ page, text }) => `--- Page ${page} ---${text}`).join('');

    /**
     * Slice the page list into one text blob per detected bill.
     * Falls back to the whole document if detection returns nothing usable.
     */
    const sliceIntoBills = (pages, bills) => {
      if (!Array.isArray(bills) || bills.length === 0) {
        return [joinPages(pages)];
      }

      const byPage = new Map(pages.map((p) => [p.page, p]));

      return bills
        .map(({ start_page: start, end_page: end }) => {
          const slice = [];
          for (let n = start; n <= end; n += 1) {
            if (byPage.has(n)) slice.push(byPage.get(n));
          }
          return slice.length ? joinPages(slice) : null;
        })
        .filter(Boolean);
    };


    const mapWithConcurrency = async (items, limit, worker) => {
      const results = new Array(items.length);
      let cursor = 0;

      const runner = async () => {
        while (cursor < items.length) {
          const index = cursor;
          cursor += 1;
          try {
            results[index] = { status: 'fulfilled', value: await worker(items[index]) };
          } catch (error) {
            results[index] = { status: 'rejected', reason: error };
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(limit, items.length) }, runner)
      );
      return results;
    };

    const askMultipleSitesConfirmation = async () => {
      const result = await Swal.fire({
        title: 'Multiple Sites Detected',
        text: 'Multiple sites are detected, would you like to upload all bills?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, upload all!',
        cancelButtonText: 'No, just this site!',
        reverseButtons: true
      });
      return result.isConfirmed;
    };

    const showErrorAlert = (message) => {
      Swal.fire({
        title: 'Error.',
        text: message,
        icon: 'warning',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#007b4e',
      });
    };

    const handleProcessingError = (err, file, tempId) => {
      console.error(err);

      if (err.message === 'cancelling') return;

      let message = 'Please try again later.';

      if (file.type === 'text/csv') {
        message = "Please limit CSV files to 10-15 lines per file.";
      } else if (err.response?.data?.includes('error')) {
        message = err.response.data.error;
      }

      showErrorAlert(message);
      updateFileProgress(tempId, 0, 'Error');
    };

    await Promise.all(
      newFiles.map(async (file) => {

        // Read file as Base64
        const fileData = await readFileAsBase64(file);

        const tempid = fileData.tempId;

        // Add file to state immediately
        setFiles((prev) => [...prev, fileData]);

        try {
          const cancelToken = API.cancelToken();

          updateFileProgress(tempid, 25, 'Processing...');
          // Process PDF if needed
          if (file.type === 'application/pdf') {
            await processPdfFile(fileData, cancelToken, tempid);
          }

          if (file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
            if (!fileData.json.length) {
              Swal.fire({
                title: 'Oops!',
                text: "No data was found!",
                icon: 'warning',
                confirmButtonText: 'Ok',
                confirmButtonColor: '#007b4e',
              });
              return
            }
            let json = fileData.json
            let keys = Object.keys(json[0])
            let dictKey = Object.keys(CSV_DICT)
            if (keys.length === dictKey.length && keys.every(k => dictKey.includes(k))) {
              updateFileProgress(tempid, 75, 'Processing...');
              fileData.source = null
              json = json.map(row =>
                Object.fromEntries(
                  Object.entries(row).map(([key, value]) => [
                    CSV_DICT[key],
                    value
                  ])
                )
              );
              // Match bills with accounts
              let bills = await matchBillsWithAccounts(json, props.accounts, props.building);

              fileData.bills = bills;

              setFiles((prev) =>
                prev.map((f) => (f.tempId === tempid ? { ...fileData } : f))
              );

              updateFileProgress(tempid, 100, 'Completed');
              return
            } else {
              Swal.fire({
                title: 'Oops!',
                text: "Uploaded file is not recognized! Please download and complete the template.",
                icon: 'warning',
                confirmButtonText: 'Ok',
                confirmButtonColor: '#007b4e',
              });
              return
            }
          }
          let billData
          if (fileData.segments.length) {
            let segments = fileData.segments
            const total = segments.length;
            let completed = 0;

            updateFileProgress(
              tempid,
              60,
              total > 1 ? `AI is analyzing ${total} bills...` : 'AI is analyzing your file...'
            );

            const settled = await mapWithConcurrency(
              segments,
              EXTRACTION_CONCURRENCY,
              async (segment) => {
                const res = await API.processImage(cancelToken, { data: segment });

                completed += 1;
                // Walk 60 -> 75 as bills come back.
                updateFileProgress(
                  tempid,
                  60 + Math.round((completed / total) * 15),
                  total > 1 ? `Analyzed ${completed} of ${total} bills...` : 'Processing...'
                );

                return res.data.length ? res.data[0] : res.data;
              }
            );

            const extracted = settled
              .filter((r) => r.status === 'fulfilled')
              .flatMap((r) => (Array.isArray(r.value) ? r.value : [r.value]))
              .filter(Boolean);

            const failures = settled.filter((r) => r.status === 'rejected');
            if (failures.length) {
              console.error('Bill extraction failures', failures.map((f) => f.reason));
              showErrorAlert(
                `${failures.length} of ${total} bills could not be read. The rest were processed.`
              );
            }

            if (!extracted.length) {
              throw new Error('No bills could be extracted from this file');
            }
            billData = [...extracted, ...failures]

          } else {
            updateFileProgress(tempid, 60, 'AI is analyzing your file...');
            billData = (await API.processImage(cancelToken, { data: fileData.data })).data || [];
          }

          updateFileProgress(tempid, 75, 'Processing...');

          // Match bills with accounts
          let bills = await matchBillsWithAccounts(billData, props.accounts, props.building);

          fileData.bills = bills;

          setFiles((prev) =>
            prev.map((f) => (f.tempId === tempid ? { ...fileData } : f))
          );

          updateFileProgress(tempid, 100, 'Completed');
        } catch (err) {
          handleProcessingError(err, file, tempid);
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.accounts, props.building]);


  useEffect(() => {
    setReady(files.every(file => file.progress === 100))
  }, [files])

  // Handle drag events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  useEffect(() => {
    if (props.method === 0) setFiles([])
    else setFiles([{ bills: [{}] }])
  }, [props.method])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(file =>
      file.type === 'text/csv' ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === 'application/pdf' ||
      file.type.startsWith('image/')
    );
    processFiles(droppedFiles);
  }, [processFiles]);

  // Handle file input change
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(file =>
      file.type === 'text/csv' ||
      file.type === 'application/pdf' ||
      file.type.startsWith('image/')
    );
    processFiles(selectedFiles);
  };

  // Trigger file input click
  const handleClick = () => {
    fileInputRef.current.click();
  };



  const handleSubmit = async () => {
    setSaving(true);

    const MANDATORY_FIELDS = [
      "utility_bill_account_id",
      "bill_month",
      "bill_year",
      "basic_monthly",
      "carbon_charge",
      "municipal_tax",
      "gst_cost",
      "pst_cost",
      "bill_id",
      "bill_url"
    ];

    const COMMON_FIELDS = [
      "utility_bill_account_id",
      "bill_url",
      "bill_id",
      "bill_month",
      "bill_year",
      "basic_monthly",
      "delivery_charges",
      "other_charges",
      "carbon_charge",
      "municipal_tax",
      "gst_cost",
      "pst_cost",
      "type"
    ];

    const TYPE_SPECIFIC_FIELDS = {
      0: ["kwh_consumption", "kwh_cost", "kva_consumption", "kva_cost"],
      1: ["gas_consumption", "gas_cost", "gas_unit"],
      2: ["water_consumption", "water_cost", "water_unit", "sewage_consumption", "sewage_cost"]
    };

    const NUMERIC_FIELDS = [
      "basic_monthly",
      "kwh_consumption",
      "kwh_cost",
      "kva_consumption",
      "kva_cost",
      "water_consumption",
      "water_cost",
      "sewage_consumption",
      "sewage_cost",
      "gas_consumption",
      "gas_cost",
      "delivery_charges",
      "other_charges",
      "carbon_charge",
      "municipal_tax",
      "gst_cost",
      "pst_cost",
      "emissions"
    ];

    const errors = new Set();

    const cleanBillData = (bill) => {
      const type = bill.type;
      const allowedFields = [...COMMON_FIELDS, ...TYPE_SPECIFIC_FIELDS[type]];

      const cleaned = {};
      allowedFields.forEach(field => {
        cleaned[field] = bill.hasOwnProperty(field) ? bill[field] : 0;
      });
      cleaned.type = type;

      return cleaned;
    };

    const validateMandatoryFields = (bill) => {
      return MANDATORY_FIELDS.every(field => bill[field] !== '');
    };

    const convertWaterUnits = (bill) => {
      if (!['water_unit', 'water_consumption', 'sewage_consumption'].every(key => key in bill)) {
        return;
      }

      const { water_unit, water_consumption, sewage_consumption } = bill;

      if (water_unit === 'm3') {
        bill.water_consumption = +(water_consumption * 1000).toFixed(2);
        bill.sewage_consumption = +(sewage_consumption * 1000).toFixed(2);
      } else if (water_unit === 'ft3') {
        bill.water_consumption = +(water_consumption * 28.3168).toFixed(2);
        bill.sewage_consumption = +(sewage_consumption * 28.3168).toFixed(2);
      }
    };

    const convertGasUnits = (bill) => {
      if (!['gas_unit', 'gas_consumption'].every(key => key in bill)) {
        return;
      }

      const { gas_unit, gas_consumption } = bill;
      const conversionFactors = {
        'ft3': 1 / 35.3147,
        'L': 1 / 1000,
        'GJ': 26.853
      };

      if (gas_unit in conversionFactors) {
        bill.gas_consumption = +(gas_consumption * conversionFactors[gas_unit]).toFixed(2);
      }
    };

    const formatNumericFields = (bill) => {
      NUMERIC_FIELDS.forEach(field => {
        if (field in bill) {
          bill[field] = bill[field] ? +parseFloat(bill[field]).toFixed(2) : 0;
        }
      });
    };

    const processBills = async (bills, billUrl) => {
      const processedBills = bills.map(cleanBillData);

      for (const bill of processedBills) {
        bill.bill_url = billUrl;

        if (!validateMandatoryFields(bill)) {
          throw new Error("Some bills are missing values.");
        }

        convertWaterUnits(bill);
        convertGasUnits(bill);
        formatNumericFields(bill);
        bill.bill_id = `${bill.bill_id}`;
      }

      return processedBills;
    };

    const uploadBills = async (bills) => {
      const cancelToken = API.cancelToken();
      return await API.postUtilityBills(cancelToken, bills);
    };

    const handleDuplicates = (file, duplicates) => {
      setFiles(prev => {
        const found = prev.find(i => i.name === file.name);
        if (!found) return prev;

        const updated = {
          ...found,
          bills: duplicates.map(e => ({ ...e, duplicate: true }))
        };

        return [
          ...prev.filter(i => i.name !== file.name),
          updated
        ];
      });
    };

    const showSuccessAlert = () => {
      Swal.fire({
        title: 'Success!',
        text: "Your bills have been successfully uploaded!",
        icon: 'success',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#007b4e',
      });
    };

    const showErrorAlert = (errors) => {
      Swal.fire({
        title: 'Oops!',
        html: `The following errors occurred:<br>${[...errors].join('<br>')}`,
        icon: 'error',
        confirmButtonText: 'Ok',
        confirmButtonColor: '#007b4e',
      });
    };

    // Main processing loop
    for (const file of files) {
      try {
        let billUrl = ['Manual Upload']
        if (file.source) {
          billUrl = await uploadAndScan([{ data: file.source, name: file.name, type: file.type }]);
          if (!billUrl.length) break
        }
        const processedBills = await processBills(file.bills, billUrl[0]);
        const response = await uploadBills(processedBills);
        if (response.data.skipped_duplicates?.length) {
          handleDuplicates(file, response.data.skipped_duplicates);
        } else {
          setFiles(prev => {
            if (props.method !== 1) return prev.filter(i => i.name !== file.name)
            else return [{ bills: [{ bill_month: "", bill_year: "", bill_id: "" }] }]
          });
        }
      } catch (error) {
        console.error(error);

        if (error.message === 'cancelling') continue;

        const errorMessage = error.response?.data?.message || error.message || "Bills failed to upload";
        errors.add(errorMessage);
      }
    }

    setSaving(false);

    if (errors.size) {
      showErrorAlert(errors);
    } else {
      showSuccessAlert();
    }
  };

  // Update progress for a specific file
  const updateFileProgress = (tempId, progress, status) => {
    setFiles((prev) =>
      prev.map((file) =>
        file.tempId === tempId ? { ...file, progress, status } : file
      )
    );
  };

  // Remove file from list
  const removeFile = (tempId) => {
    setFiles((prev) => prev.filter((file) => file.tempId !== tempId));
  };

  return (
    <div className="mx-auto p-4 perspective-1000">
      {
        !props.accounts &&
        <Loader />
      }
      {
        !props.method && props.accounts && !files.length &&
        <div className="w-full flex flex-nowrap items-center justify-end cursor-pointer">
          {GlobalSVG.download()}
          <a href='https://gw-img-bckt-production.s3.ca-central-1.amazonaws.com/templates/utility_bill_template.xlsx'><p className='mx-2' >Template</p></a>
          <Tooltip
            text="
            <ul>
            <li>- Ensure the first 3 columns of the Excel template are in Text format.</li>
            <br>
            <li>- Ensure that you only fill out columns relevant to the type of data per line (e.g water and gas related columns stay empty when uploading electrical data).</li>
            <br>
            <li>- You can upload information for multiple bill types, utilities, etc. as long as the information uploaded is all within the same portfolio.</li>
            </ul>"
            right="0"
            left="-1000%"
            top="100%"
            width="300px"
            height="200px"
            textAlign="left"
            show={true}>
            {GlobalSVG.info("black")}
          </Tooltip>
        </div>
      }
      {
        !props.method && props.accounts && !files.length ?
          <div className="flex flex-nowrap items-center justify-center w-full">
            <div
              className={`max-w-2xl border-2 border-dashed rounded-lg p-8 text-center transition-colors transform rotate-x-2 rotate-y-2 shadow-xl hover:shadow-2xl ${dragOver ? 'border-[#007b4e] bg-[#4fa78b]' : 'border-[gray] bg-[white]'
                }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv,.pdf,image/*"
                multiple
                className="hidden"
              />
              <p className="text-lg font-medium text-gray-700 transform translate-z-10">
                Drag and drop your files here or{' '}
                <button
                  onClick={handleClick}
                  className="text-[#007b4e] underline focus:outline-none"
                >
                  click to upload
                </button>
              </p>
              <p className="text-sm text-gray-500 mt-2 transform translate-z-5">
                Supported formats: CSV, PDF, Images
              </p>
            </div>

          </div>
          :
          null
      }

      {
        !props.method && files.length > 0 && (
          <div className="mt-4 transform rotate-x-1 rotate-y-1" style={{ transformStyle: 'preserve-3d' }}>
            <h3 className="text-lg font-semibold text-gray-800 transform translate-z-10">Processed Bills:</h3>
            <ul className="mt-2 space-y-2">
              {files.map((file) => (
                <li
                  key={file.tempId}
                  className="flex flex-col p-2 bg-[white] border rounded-lg transform rotate-x-3 rotate-y-3 shadow-md hover:shadow-xl transition-shadow"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <div className="flex justify-between items-center transform translate-z-5 p-2">
                    <span className="text-sm text-gray-700">
                      {file.name} ({(file.size / 1024).toFixed(2)} KB) - <strong>{file.status}</strong>
                    </span>
                    <button
                      onClick={() => removeFile(file.tempId)}
                      className=""
                    >
                      {GlobalSVG.false()}
                    </button>
                  </div>
                  <div className="mt-1 bg-[gray] rounded-full h-2.5">
                    <div
                      className="bg-[#a3d237] h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${file.progress}%` }}
                    ></div>
                  </div>
                  {
                    file.bills && file.bills.map((c, i) => (
                      <UtilityBillLineUpload key={i + "utilitybill"} value={c} type={c.type} setFiles={setFiles} fileIndex={file.tempId} billIndex={i} accounts={props.accounts} />
                    ))
                  }
                </li>
              ))}
            </ul>

            {ready && (saving ?
              <div className='mt-2'>
                <LoaderWheel />
              </div>
              :
              <button
                onClick={handleSubmit}
                className="mt-4 bg-[#007b4e] text-white px-4 py-2 rounded-lg hover:bg-[#007b4e] focus:outline-none transform rotate-x-2 shadow-md hover:shadow-lg translate-z-10"
                style={{ transformStyle: 'preserve-3d' }}
              >
                Save
              </button>)
            }
          </div>
        )
      }

      {
        props.method === 1 ?
          <div className="mt-4 transform rotate-x-1 rotate-y-1" style={{ transformStyle: 'preserve-3d' }}>
            <h3 className="text-lg font-semibold text-gray-800 transform translate-z-10">New Bill:</h3>

            <UtilityBillLineUpload type={files[0]?.bills[0]?.type} value={files[0]?.bills[0]} files={files} setFiles={setFiles} fileIndex={0} billIndex={0} accounts={props.accounts && props.accounts.filter(e => e.building_id === props.building)} />
            {saving ?
              <div className='mt-2'>
                <LoaderWheel />
              </div>
              :
              <button
                onClick={handleSubmit}
                className="mt-4 bg-[#007b4e] text-white px-4 py-2 rounded-lg hover:bg-[#007b4e] focus:outline-none transform rotate-x-2 shadow-md hover:shadow-lg translate-z-10"
                style={{ transformStyle: 'preserve-3d' }}
              >
                Save
              </button>}
          </div>
          :
          null
      }
    </div >
  );
};

export default FileUploadContainer